// frontend/src/views/admin/DocumentManagement.tsx

import React, { useState, useEffect } from "react";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import {
  Upload,
  File,
  ImageIcon,
  Eye,
  EyeOff,
  Trash2,
  X,
  Loader2,
} from "lucide-react";
import { useDocuments } from "../../contexts/DocumentsContext";
import api from "../../services/api";
import { Document } from "../../services/api";

type DocumentType = "image" | "pdf";

interface UploadFile {
  file: File;
  title: string;
  examDate: string;
  tags: string[];
  type: DocumentType;
  tagInput: string;
}

const UploadForm: React.FC = () => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { refreshDocuments } = useDocuments();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).map((file) => ({
      file,
      title: "",
      examDate: "",
      tags: [],
      tagInput: "",
      type: file.type.startsWith("image/")
        ? ("image" as DocumentType)
        : ("pdf" as DocumentType),
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleAddTags = (
    e: React.MouseEvent | React.KeyboardEvent,
    index: number
  ) => {
    if (e.type === "keypress") {
      const keyboardEvent = e as React.KeyboardEvent;
      if (keyboardEvent.key !== "Enter") return;
      keyboardEvent.preventDefault();
    } else {
      (e as React.MouseEvent).preventDefault();
    }

    const file = files[index];
    if (!file.tagInput?.trim()) return;

    const newFiles = [...files];
    const newTags = file.tagInput
      .split(",")
      .map((t: string) => t.trim())
      .filter((t: string) => !newFiles[index].tags.includes(t));

    newFiles[index] = {
      ...file,
      tags: [...file.tags, ...newTags],
      tagInput: "",
    };

    setFiles(newFiles);
  };

  const handleRemoveTag = (
    e: React.MouseEvent,
    fileIndex: number,
    tagToRemove: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const newFiles = [...files];
    newFiles[fileIndex] = {
      ...files[fileIndex],
      tags: files[fileIndex].tags.filter((tag) => tag !== tagToRemove),
    };
    setFiles(newFiles);
  };

  const handleUpload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!files.length) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach((fileData, index) => {
        formData.append(`file${index}`, fileData.file);
        const metadata = {
          title: fileData.title,
          examDate: fileData.examDate,
          tags: fileData.tags,
        };
        formData.append(`metadata${index}`, JSON.stringify(metadata));
      });

      const response = await api.post("/documents/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setFiles([]);
      await refreshDocuments();
    } catch (err: any) {
      // Handle error
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm border">
      <h2 className="text-xl font-semibold mb-4">Nuovo Documento</h2>

      <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center mb-6">
        <input
          type="file"
          id="file-upload"
          className="hidden"
          multiple
          accept=".pdf,image/*"
          onChange={handleFileSelect}
        />
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center cursor-pointer"
        >
          <Upload className="w-12 h-12 text-gray-400 mb-2" />
          <span className="text-gray-600">
            Trascina i file qui o clicca per selezionarli
          </span>
          <span className="text-sm text-gray-500 mt-1">PDF e immagini</span>
        </label>
      </div>

      {files.map((file, index) => (
        <div key={index} className="border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {file.type === "pdf" ? (
                <File className="text-gray-400" size={24} />
              ) : (
                <ImageIcon className="text-gray-400" size={24} />
              )}
              <span className="font-medium">{file.file.name}</span>
            </div>
            <button
              type="button"
              className="p-2 text-gray-600 hover:text-gray-900 border rounded-lg"
              onClick={(e) => {
                e.preventDefault();
                setFiles(files.filter((_, i) => i !== index));
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={file.title}
                onChange={(e) => {
                  const newFiles = [...files];
                  newFiles[index] = {
                    ...file,
                    title: e.target.value,
                  };
                  setFiles(newFiles);
                }}
                placeholder="Inserisci un titolo descrittivo"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Esame *
              </label>
              <input
                type="date"
                value={file.examDate}
                onChange={(e) => {
                  const newFiles = [...files];
                  newFiles[index] = {
                    ...file,
                    examDate: e.target.value,
                  };
                  setFiles(newFiles);
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Aggiungi tags (separati da virgola)"
                value={file.tagInput}
                onChange={(e) => {
                  const newFiles = [...files];
                  newFiles[index] = {
                    ...file,
                    tagInput: e.target.value,
                  };
                  setFiles(newFiles);
                }}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => handleAddTags(e, index)}
              />
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                onClick={(e) => handleAddTags(e, index)}
              >
                Aggiungi
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {file.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveTag(e, index, tag)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}

      {files.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                <span>Caricamento...</span>
              </>
            ) : (
              <>
                <Upload size={16} />
                <span>
                  Carica {files.length} {files.length === 1 ? "file" : "files"}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

const DocumentRow: React.FC<{
  doc: Document;
  onToggleVisibility: (
    e: React.MouseEvent,
    id: number,
    isVisible: boolean
  ) => void;
  onDelete: (e: React.MouseEvent, id: number) => void;
}> = React.memo(({ doc, onToggleVisibility, onDelete }) => {
  return (
    <tr className="border-b">
      <td className="p-4">
        <div>
          <div className="font-medium">{doc.title}</div>
          <div className="text-sm text-gray-500">{doc.filename}</div>
        </div>
      </td>
      <td className="p-4">
        <div className="flex items-center">
          {doc.type === "pdf" ? (
            <File className="text-gray-400 mr-2" size={20} />
          ) : (
            <ImageIcon className="text-gray-400 mr-2" size={20} />
          )}
          {doc.type.toUpperCase()}
        </div>
      </td>
      <td className="p-4">{new Date(doc.exam_date).toLocaleDateString()}</td>
      <td className="p-4">
        <div className="flex flex-wrap gap-1">
          {doc.tags?.map((tag: string) => (
            <span
              key={tag}
              className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600"
            >
              {tag}
            </span>
          ))}
        </div>
      </td>
      <td className="p-4">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
            doc.is_visible
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {doc.is_visible ? "Visibile" : "Nascosto"}
        </span>
      </td>
      <td className="p-4">
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className={`p-2 rounded-lg hover:bg-gray-100 ${
              doc.is_visible ? "text-gray-600" : "text-green-600"
            }`}
            onClick={(e) => onToggleVisibility(e, doc.id, doc.is_visible)}
          >
            {doc.is_visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button
            type="button"
            className="p-2 rounded-lg hover:bg-gray-100 text-red-600"
            onClick={(e) => onDelete(e, doc.id)}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
});

const DocumentsTable: React.FC = React.memo(() => {
  const {
    documents,
    loading,
    error,
    sortDirection,
    setSortDirection,
    toggleDocumentVisibility,
    setIsAdminView,
    refreshDocuments,
  } = useDocuments();
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
    null
  );

  useEffect(() => {
    setIsAdminView(true);
    return () => setIsAdminView(false);
  }, [setIsAdminView]);

  const handleToggleVisibility = async (
    e: React.MouseEvent,
    docId: number,
    currentVisibility: boolean
  ) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await toggleDocumentVisibility(docId, currentVisibility);
    } catch (err) {
      // Handle error
    }
  };

  const handleDelete = async (docId: number) => {
    try {
      await api.delete(`/documents/${docId}`);
      await refreshDocuments();
    } catch (err) {
      // Handle error
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!documentToDelete) return;
    await handleDelete(documentToDelete.id);
    setDocumentToDelete(null);
  };

  const handleSort = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newDirection = sortDirection === "DESC" ? "ASC" : "DESC";
    setSortDirection(newDirection);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="animate-spin" />
        <span className="ml-2">Caricamento documenti...</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 p-4 text-center">{error}</div>;
  }

  return (
    <>
      <div className="mt-8 bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-medium">Documenti</h2>
          <button
            type="button"
            onClick={handleSort}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            {sortDirection === "DESC"
              ? "Più recenti prima"
              : "Più vecchi prima"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4">Documento</th>
                <th className="text-left p-4">Tipo</th>
                <th className="text-left p-4">Data Esame</th>
                <th className="text-left p-4">Tags</th>
                <th className="text-left p-4">Stato</th>
                <th className="text-center p-4">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  onToggleVisibility={handleToggleVisibility}
                  onDelete={(e, id) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocumentToDelete(doc);
                  }}
                />
              ))}
              {documents.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    Nessun documento presente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirmed}
        itemName={`"${documentToDelete?.title}"`}
        itemType="Document"
      />
    </>
  );
});

const DocumentManagement: React.FC = () => {
  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Gestione Documenti</h1>
      <UploadForm />
      <DocumentsTable />
    </div>
  );
};

export default DocumentManagement;
